import { useEffect, useState } from "react"

import { UserButton, UserProfile, useUser } from "@clerk/react"
import { Link, Navigate } from "@tanstack/react-router"

import { FoundationLayout } from "@/components/app/foundation-layout"
import { AccountSessionSkeleton } from "@/components/app/public-loading-skeleton"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { isFleetSuperAdminRoleToken } from "@/features/operator/shared/operator-access"
import { apiClient } from "@/lib/api/client"
import { operatorShellLinks, publicShellLinks } from "@/lib/surfaces"

type SessionBootstrapState =
  | {
      status: "loading"
    }
  | {
      destination: string | null
      organizationId: string | null
      organizationName: string | null
      role: string | null
      status: "ready"
    }
  | {
      message: string
      status: "error"
    }

const FLEET_COMPANY_DETAILS_ONBOARDING_PATH = "/fleet/onboarding/organization"

function normalizeRoleToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

export function AccountHomePage() {
  const { isLoaded, user } = useUser()
  const [sessionState, setSessionState] = useState<SessionBootstrapState>({
    status: "loading",
  })

  useEffect(() => {
    let cancelled = false

    if (!isLoaded || !user) {
      return
    }

    void apiClient
      .getCurrentSession()
      .then((session) => {
        if (cancelled) {
          return
        }

        setSessionState({
          destination:
            session.data.postAuthDestination?.trim() || "/account",
          organizationId: session.data.organization?.id ?? null,
          organizationName: session.data.organization?.name ?? null,
          role: session.data.user.role ?? null,
          status: "ready",
        })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }

        setSessionState({
          message:
            error instanceof Error
              ? error.message
              : "Unable to load your account right now.",
          status: "error",
        })
      })

    return () => {
      cancelled = true
    }
  }, [isLoaded, user])

  const workspaceDestination =
    sessionState.status === "ready" ? sessionState.destination : null
  const roleToken =
    sessionState.status === "ready"
      ? normalizeRoleToken(sessionState.role)
      : ""
  const isFleetSafetySuperAdmin =
    sessionState.status === "ready"
    && isFleetSuperAdminRoleToken(roleToken)
  const shouldRedirectToFleetWorkspace = Boolean(
    workspaceDestination
      && (
        workspaceDestination === "/fleet"
        || workspaceDestination.startsWith("/fleet/")
      )
  )

  if (shouldRedirectToFleetWorkspace) {
    return (
      <Navigate
        to={
          isFleetSafetySuperAdmin
            ? (workspaceDestination as string)
            : FLEET_COMPANY_DETAILS_ONBOARDING_PATH
        }
        replace
      />
    )
  }

  if (workspaceDestination === "/account/onboarding") {
    return <Navigate to="/account/onboarding" replace />
  }

  const workspaceLink = workspaceDestination
    ? operatorShellLinks.find((link) => {
        return (
          workspaceDestination === link.to
          || workspaceDestination.startsWith(`${link.to}/`)
        )
      }) ?? null
    : null

  return (
    <FoundationLayout
      title="Your account"
      description="Manage your SafetyQR account settings, view your role, and access your workspace."
      links={publicShellLinks}
    >
      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_20rem]">
        <Card className="rounded-4xl border-border/70">
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-2">
                <CardTitle className="font-heading text-3xl">
                  {user?.fullName || user?.primaryEmailAddress?.emailAddress || "SafetyQR user"}
                </CardTitle>
                <CardDescription className="text-base leading-7">
                  Manage your authentication and account settings.
                </CardDescription>
              </div>
              <UserButton />
            </div>
          </CardHeader>
          <CardContent>
            <UserProfile />
          </CardContent>
        </Card>

        <div className="grid gap-5">
          {sessionState.status === "loading" ? (
            <AccountSessionSkeleton testId="account-session-skeleton" />
          ) : null}

          {sessionState.status === "ready" ? (
            <Card className="rounded-4xl border-border/70 bg-background/70">
              <CardHeader>
                <CardTitle>Account details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                <div className="rounded-3xl border border-border/70 bg-background/80 px-4 py-3">
                  Role: <span className="font-medium text-foreground">{sessionState.role ?? "viewer"}</span>
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/80 px-4 py-3">
                  Organization:{" "}
                  <span className="font-medium text-foreground">{sessionState.organizationName ?? "Personal"}</span>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {sessionState.status === "error" ? (
            <Card className="rounded-4xl border-border/70 bg-background/70">
              <CardContent className="pt-6">
                <div className="rounded-3xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {sessionState.message}
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card className="rounded-4xl border-border/70 bg-background/70">
            <CardHeader>
              <CardTitle>Quick links</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <Button asChild className="justify-start">
                <Link to="/register">Get Safety QR</Link>
              </Button>
              {workspaceLink ? (
                <Button asChild variant="outline" className="justify-start">
                  <Link to={workspaceLink.to}>{workspaceLink.label} workspace</Link>
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </section>
    </FoundationLayout>
  )
}
